// Whether the window a publisher is looking at can ever reach the CTR power gate.
//
// Pure, and out here rather than in AnalyticsPage, so it can be tested without
// pulling a page component and the router in behind it.

// Statistical power gate (overlay-ctr-truth spec): ~6,500 qualified views for
// 80% power to distinguish 0.15% from 0.3% CTR. Below this, CTR is shown but any
// viability verdict is explicitly deferred.
export const POWER_GATE_VIEWS = 6500;

export interface CtrReachability {
  text: string;
  /** Days at the current rate to reach the gate, or null when unknowable. */
  daysNeeded: number | null;
}

const DEFER = 'Judge CTR only after that.';

/**
 * The gate note used to say only "6,488 to go", which reads as "keep going".
 *
 * On `Today` that is advice about a window which resets at midnight. EVERY period
 * on this screen is bounded, so "collect more" is achievable only when
 * rate x window >= 6,500 — naming the window that WOULD work turns a dead end
 * into a choice, and saying that none of them will is worth knowing early: it
 * means CTR is not going to be this publisher's metric for months, and the exact
 * counts above it are what they should be reading instead.
 */
export function ctrReachability(views: number, daysInWindow: number): CtrReachability {
  if (daysInWindow <= 0 || views <= 0) return { text: DEFER, daysNeeded: null };

  const perDay = views / daysInWindow;
  const daysNeeded = Math.ceil(POWER_GATE_VIEWS / perDay);

  // Already a long enough window and still short of the gate: advice about a
  // longer one would be noise, because length is not what is missing.
  if (daysNeeded <= daysInWindow) return { text: DEFER, daysNeeded };

  if (daysNeeded <= 30) return { text: `At this rate that is about ${daysNeeded} days — try 30d.`, daysNeeded };
  if (daysNeeded <= 90) return { text: `At this rate that is about ${daysNeeded} days — try 90d.`, daysNeeded };

  const months = Math.round(daysNeeded / 30);
  return {
    text:
      `At this rate that is roughly ${months} months of traffic, so no window here will settle it yet — ` +
      'read the counts above instead, which are exact at any volume.',
    daysNeeded,
  };
}
